import React from "react";
import {
  Grid,
  Paper,
  Stack,
  Typography,
  Link,
  Chip,
  Box,
} from "@mui/material";

import PlaceIcon from "@mui/icons-material/Place";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import ScienceIcon from "@mui/icons-material/Science";

function InfoCard({ icon, title, value, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: "100%",
        borderRadius: 5,
        background: "linear-gradient(135deg,#FFFFFF 0%,#F7FBFC 100%)",
        border: "1px solid #E4EDF2",
        transition: ".35s",
        boxShadow: "0 8px 25px rgba(11,60,93,.05)",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 15px 35px rgba(11,60,93,.12)",
        },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: "#EEF5FA",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#0B3C5D",
          }}
        >
          {icon}
        </Box>

        <Typography
          sx={{
            fontWeight: 700,
            color: "#0B3C5D",
            fontSize: "1.05rem",
          }}
        >
          {title}
        </Typography>
      </Stack>

      {children ? (
        children
      ) : (
        <Typography
          sx={{
            color: "#5B6770",
            lineHeight: 1.8,
            fontSize: 15,
          }}
        >
          {value}
        </Typography>
      )}
    </Paper>
  );
}

function HeroInfo({ plant }) {
  return (
    <Grid container spacing={3} mb={5}>
      {/* Local Name */}
      <Grid item xs={12} sm={6} lg={4}>
        <InfoCard
          icon={<PlaceIcon />}
          title="Local Name"
          value={plant.local_name}
        />
      </Grid>

      {/* Scientific Name */}
      <Grid item xs={12} sm={6} lg={4}>
        <InfoCard
          icon={<ScienceIcon />}
          title="Scientific Name"
        >
          <Typography
            sx={{
              fontStyle: "italic",
              color: "#555",
              lineHeight: 1.8,
              fontSize: 15,
            }}
          >
            {plant.scientific_name}
          </Typography>
        </InfoCard>
      </Grid>

      {/* Family */}
      <Grid item xs={12} sm={6} lg={4}>
        <InfoCard
          icon={<LocalFloristIcon />}
          title="Family"
        >
          <Chip
            label={
              plant.family ||
              plant.botanical_profile?.family ||
              plant.taxonomic_classification?.family ||
              "Not Available"
            }
            sx={{
              bgcolor: "#EEF8F2",
              color: "#2E7D32",
              fontWeight: 700,
              borderRadius: 2,
            }}
          />
        </InfoCard>
      </Grid>

      {/* Digital Flora URL + QR Code */}
      <Grid item xs={12}>
        <InfoCard
          icon={<QrCode2Icon />}
          title="Digital Flora"
        >
          <Grid container spacing={3} alignItems="center">
            {/* URL */}
            <Grid item xs={12} md={8}>
              <Typography
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: "#0B3C5D",
                }}
              >
                Scan the QR Code or visit:
              </Typography>

              <Link
                href={plant.qr_url}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{
                  color: "#1565C0",
                  fontWeight: 600,
                  wordBreak: "break-word",
                }}
              >
                {plant.qr_url}
              </Link>
            </Grid>

            {/* QR Code */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: {
                  xs: "center",
                  md: "flex-end",
                },
              }}
            >
              <Box
                component="img"
                src={plant.qr_code}
                alt={`${plant.scientific_name} QR Code`}
                sx={{
                  width: 150,
                  height: 150,
                  p: 1,
                  bgcolor: "#fff",
                  borderRadius: 3,
                  border: "1px solid #ddd",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
                  transition: "0.3s",
                  "&:hover": {
                    transform: "scale(1.08)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.20)",
                  },
                }}
              />
            </Grid>
          </Grid>
        </InfoCard>
      </Grid>
    </Grid>
  );
}

export default HeroInfo;